import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, ChevronDown, ChevronUp, Mail, Phone, MessageCircle } from 'lucide-react'
import '../styles/FAQPage.css'

export default function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [openSections, setOpenSections] = useState({})

  const faqSections = [
    {
      id: 'general',
      title: 'Questions générales',
      icon: 'ℹ️',
      questions: [
        {
          q: 'Qu\'est-ce qu\'UltimaTag ?',
          a: 'UltimaTag est un service de protection d\'objets personnels utilisant des stickers QR codes uniques. En cas de perte, toute personne qui trouve votre objet peut scanner le QR code pour vous contacter anonymement.'
        },
        {
          q: 'Comment fonctionne UltimaTag ?',
          a: '1. Commandez vos stickers QR uniques\n2. Collez-les sur vos objets importants\n3. Enregistrez vos objets sur notre plateforme\n4. Si perdu, scannez le QR code pour retrouver le propriétaire'
        },
        {
          q: 'Quels objets puis-je protéger ?',
          a: 'Tous vos objets de valeur : téléphones, ordinateurs portables, clés, portefeuilles, sacs, montres, appareils photo, documents importants, etc.'
        }
      ]
    },
    {
      id: 'commands',
      title: 'Commandes et Paiement',
      icon: '🛒',
      questions: [
        {
          q: 'Comment commander mes stickers ?',
          a: 'Rendez-vous sur notre page de tarifs, choisissez votre pack (5, 10 ou 20 stickers) et suivez le processus de commande sécurisé.'
        },
        {
          q: 'Quels modes de paiement acceptez-vous ?',
          a: 'Nous acceptons les paiements par Mobile Money (Orange Money, MTN Mobile Money, Moov Money, Wave) via CinetPay, ainsi que les cartes bancaires.'
        },
        {
          q: 'Quand vais-je recevoir mes stickers ?',
          a: 'À Abidjan : livraison en 24-48h\nAutres villes : 3-7 jours selon votre localisation\nVous recevrez un SMS avec le suivi de livraison.'
        },
        {
          q: 'Puis-je suivre ma commande ?',
          a: 'Oui, un numéro de suivi vous est envoyé par SMS dès l\'expédition de votre commande.'
        }
      ]
    },
    {
      id: 'usage',
      title: 'Utilisation des stickers',
      icon: '📱',
      questions: [
        {
          q: 'Comment coller mes stickers ?',
          a: '1. Nettoyez la surface avec un chiffon sec\n2. Décolllez le sticker du support\n3. Appliquez-le sur une surface propre et plane\n4. Appuyez fermement pendant 30 secondes'
        },
        {
          q: 'Les stickers sont-ils résistants ?',
          a: 'Nos stickers sont waterproof, résistants aux UV et conçus pour durer plusieurs années, même en usage intensif.'
        },
        {
          q: 'Puis-je retirer et réutiliser un sticker ?',
          a: 'Non, nos stickers sont conçus pour une utilisation unique. Une fois collé, il ne peut être retiré sans l\'endommager.'
        },
        {
          q: 'Que faire si mon sticker s\'abîme ?',
          a: 'Contactez notre support pour obtenir un sticker de remplacement. Nous offrons une garantie de 6 mois sur nos produits.'
        }
      ]
    },
    {
      id: 'security',
      title: 'Sécurité et Confidentialité',
      icon: '🔒',
      questions: [
        {
          q: 'Mes données sont-elles sécurisées ?',
          a: 'Absolument. Nous utilisons un cryptage de bout en bout. Votre numéro de téléphone n\'est jamais révélé aux personnes qui scannent le QR code.'
        },
        {
          q: 'Comment fonctionne le système de contact ?',
          a: 'Le système utilise un numéro de relais temporaire. La personne qui trouve votre objet vous envoie un SMS anonyme, et vous pouvez ensuite communiquer directement.'
        },
        {
          q: 'Puis-je changer mes coordonnées ?',
          a: 'Oui, vous pouvez mettre à jour vos coordonnées à tout moment depuis votre tableau de bord.'
        },
        {
          q: 'Que se passe-t-il si je perds mon objet ?',
          a: 'Dès qu\'une personne scanne votre QR code, vous recevez une notification instantanée avec les instructions pour récupérer votre objet.'
        }
      ]
    },
    {
      id: 'support',
      title: 'Support et Assistance',
      icon: '💬',
      questions: [
        {
          q: 'Comment contacter le support ?',
          a: 'Email : support@ultimatag.com\nTéléphone : +225 07 12 20 49 86 \nChat en ligne : disponible sur notre site\nHoraires : Lun-Sam, 8h-20h'
        },
        {
          q: 'Quelle est votre politique de remboursement ?',
          a: 'Nous offrons une garantie satisfaction de 14 jours. Si vous n\'êtes pas satisfait, contactez-nous pour un remboursement ou un remplacement.'
        },
        {
          q: 'Proposez-vous des packs pour entreprises ?',
          a: 'Oui, contactez notre service commercial pour des packs personnalisés pour votre entreprise.'
        },
        {
          q: 'Comment signaler un problème technique ?',
          a: 'Utilisez notre formulaire de contact ou envoyez un email à tech@ultimatag.com avec une description détaillée du problème.'
        }
      ]
    }
  ]

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const filteredSections = faqSections.map(section => ({
    ...section,
    questions: section.questions.filter(q => 
      q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.questions.length > 0)

  const contactMethods = [
    {
      title: 'Email',
      description: 'support@ultimatag.com',
      details: 'Réponse sous 24h',
      icon: <Mail size={24} />,
      color: '#0066FF'
    },
    {
      title: 'Téléphone',
      description: '+225 XX XX XX XX',
      details: 'Lun-Sam, 8h-17h',
      icon: <Phone size={24} />,
      color: '#00B894'
    },
    {
      title: 'Chat en ligne',
      description: 'Disponible sur le site',
      details: 'Réponse instantanée',
      icon: <MessageCircle size={24} />,
      color: '#FF6B35'
    }
  ]

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <h1 className="faq-title">Foire aux Questions</h1>
          <p className="faq-subtitle">
            Trouvez rapidement des réponses à vos questions sur UltimaTag
          </p>
          
          <div className="faq-search">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher une question..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="quick-stats">
            <div className="stat">
              <div className="stat-number">{faqSections.length}</div>
              <div className="stat-label">Catégories</div>
            </div>
            <div className="stat">
              <div className="stat-number">{faqSections.reduce((acc, section) => acc + section.questions.length, 0)}</div>
              <div className="stat-label">Questions</div>
            </div>
            <div className="stat">
              <div className="stat-number">24h</div>
              <div className="stat-label">Support réactif</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <div className="faq-content">
        <div className="container">
          {/* FAQ Sections */}
          <div className="faq-sections">
            {filteredSections.length > 0 ? (
              filteredSections.map(section => (
                <div key={section.id} className="faq-section">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="faq-section-header"
                  >
                    <div className="section-header-left">
                      <span className="section-icon">{section.icon}</span>
                      <h2 className="section-title">{section.title}</h2>
                    </div>
                    {openSections[section.id] ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  
                  {(openSections[section.id] || openSections[section.id] === undefined) && (
                    <div className="faq-questions">
                      {section.questions.map((item, index) => (
                        <div key={index} className="faq-item">
                          <h3 className="question">{item.q}</h3>
                          <p className="answer">{item.a}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                <Search size={48} />
                <h3>Aucune question trouvée</h3>
                <p>Essayez d'autres mots-clés ou contactez notre support</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="btn btn-outline"
                >
                  Effacer la recherche
                </button>
              </div>
            )}
          </div>

          {/* Contact Section */}
          <div className="contact-section">
            <h2 className="contact-title">Vous n'avez pas trouvé de réponse ?</h2>
            <p className="contact-subtitle">
              Notre équipe de support est disponible pour vous aider
            </p>
            
            <div className="contact-methods">
              {contactMethods.map((method, index) => (
                <div key={index} className="contact-card">
                  <div 
                    className="contact-icon"
                    style={{ backgroundColor: method.color }}
                  >
                    {method.icon}
                  </div>
                  <h3 className="contact-method-title">{method.title}</h3>
                  <p className="contact-method-description">{method.description}</p>
                  <small className="contact-method-details">{method.details}</small>
                </div>
              ))}
            </div>
            
            <div className="contact-cta">
              <Link to="/contact" className="btn btn-primary">
                Contacter le support
              </Link>
              <Link to="/pricing" className="btn btn-outline">
                Voir les packs
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div className="quick-links-section">
            <h2 className="links-title">Liens rapides</h2>
            <div className="links-grid">
              <Link to="/how-to-use" className="quick-link">
                <div className="link-icon">📖</div>
                <div className="link-content">
                  <h3>Guide d'utilisation</h3>
                  <p>Comment utiliser vos stickers</p>
                </div>
              </Link>
              
              <Link to="/legal" className="quick-link">
                <div className="link-icon">⚖️</div>
                <div className="link-content">
                  <h3>Mentions légales</h3>
                  <p>Informations légales</p>
                </div>
              </Link>
              
              <Link to="/privacy" className="quick-link">
                <div className="link-icon">🔒</div>
                <div className="link-content">
                  <h3>Confidentialité</h3>
                  <p>Protection des données</p>
                </div>
              </Link>
              
              <Link to="/terms" className="quick-link">
                <div className="link-icon">📝</div>
                <div className="link-content">
                  <h3>Conditions</h3>
                  <p>Conditions d'utilisation</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}